import { Lightning } from '@lightningjs/sdk';

export interface VideoEvent {
  event: Event;
  videoElement: HTMLMediaElement;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AbstractPlayerEventConsumerTemplateSpec
  extends Lightning.Component.TemplateSpec {}

export default abstract class AbstractPlayerEventConsumer<
  TemplateSpec extends Lightning.Component.TemplateSpec = Lightning.Component.TemplateSpec,
> extends Lightning.Component<TemplateSpec> {
  static playerSignals = {
    $videoManagerAdBreakEnded: true,
    $videoManagerAdBreakStarted: true,
    $videoManagerBeforeSeek: true,
    $videoManagerCleanupAdPod: true,
    $videoManagerComplete: true,
    $videoManagerSeekPause: true,
    $videoManagerSkipAdPod: true,
    $videoManagerStarted: true,
    $videoPlayerAbort: true,
    $videoPlayerCanPlay: true,
    $videoPlayerCanPlayThrough: true,
    $videoPlayerClear: true,
    $videoPlayerDurationChange: true,
    $videoPlayerEmptied: true,
    $videoPlayerEncrypted: true,
    $videoPlayerEnded: true,
    $videoPlayerError: true,
    $videoPlayerEvent: true,
    $videoPlayerInterruptBegin: true,
    $videoPlayerInterruptEnd: true,
    $videoPlayerLoadedData: true,
    $videoPlayerLoadedMetadata: true,
    $videoPlayerLoadStart: true,
    $videoPlayerPlay: true,
    $videoPlayerPause: true,
    $videoPlayerPlaying: true,
    $videoPlayerProgress: true,
    $videoPlayerRatechange: true,
    $videoPlayerSeeked: true,
    $videoPlayerSeeking: true,
    $videoPlayerStalled: true,
    $videoPlayerTimeUpdate: true,
    $videoPlayerUnloading: true,
    $videoPlayerVolumeChange: true,
    $videoPlayerWaiting: true,
  } as const;

  abstract $videoPlayerEvent(type: string, event: any, ...args: any): void;

  /*
      Lightning Events are used as a basis for other players

      Lightning Events listed here: https://github.com/Metrological/metrological-sdk/blob/master/docs/plugins/videoplayer.md
      "$videoPlayerPause" is missing from this list, but seems to be implemented
    */

  // Below are the default implementations
  $videoPlayerAbort(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerAbort', rawTime);
  }

  $videoPlayerCanPlay(
    videoEvt: VideoEvent | null,
    rawTime: number,
    willResume?: boolean,
  ) {
    this.signal('$videoPlayerCanPlay', rawTime, willResume);
  }

  $videoPlayerCanPlayThrough(
    videoEvt: VideoEvent | null,
    rawTime: number,
    willResume?: boolean,
  ) {
    this.signal('$videoPlayerCanPlayThrough', rawTime, willResume);
  }

  $videoPlayerWaiting(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerWaiting', rawTime);
  }

  $videoPlayerDurationChange(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerDurationChange', rawTime);
  }

  $videoPlayerEmptied(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerEmptied', rawTime);
  }

  $videoPlayerEncrypted(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerEncrypted', rawTime);
  }

  $videoPlayerEnded(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerEnded', rawTime);
  }

  $videoPlayerError(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerError', rawTime);
  }

  $videoPlayerInterruptBegin(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerInterruptBegin', rawTime);
  }

  $videoPlayerInterruptEnd(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerInterruptEnd', rawTime);
  }

  $videoPlayerLoadedData(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerLoadedData', rawTime);
  }

  $videoPlayerLoadedMetadata(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerLoadedMetadata', rawTime);
  }

  $videoPlayerLoadStart(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerLoadStart', rawTime);
  }

  $videoPlayerPlay(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerPlay', rawTime);
  }

  $videoPlayerPause(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerPause', rawTime);
  }

  $videoPlayerPlaying(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerPlaying', rawTime);
  }

  $videoPlayerProgress(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerProgress', rawTime);
  }

  $videoPlayerRatechange(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerRatechange', rawTime);
  }

  $videoPlayerSeeked(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerSeeked', rawTime);
  }

  $videoPlayerSeeking(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerSeeking', rawTime);
  }

  $videoPlayerStalled(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerStalled', rawTime);
  }

  $videoPlayerTimeUpdate(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerTimeUpdate', rawTime);
  }

  $videoPlayerVolumeChange(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerVolumeChange', rawTime);
  }

  $videoPlayerClear(videoEvt: VideoEvent | null, rawTime: number) {
    this.signal('$videoPlayerClear', rawTime);
  }

  $videoManagerStarted(adEvent: any) {
    this.signal('$videoManagerStarted', adEvent);
  }
}
